import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

/**
 * Filled-light CTA look for Blog Grid.
 * Must target the <a> — child p/.chakra-text cannot cancel the link's text-decoration.
 * Applied on container (covers bottom CTA + CF-055 inlined featured CTA) and ctaContainer.
 */
const blogGridFilledLightCtaSx = {
  '& a.link--filled-light, & .link--filled-light': {
    alignItems: 'center',
    bg: 'rgba(27, 26, 24, 0.9)',
    borderRadius: '4px',
    borderWidth: 0,
    boxSizing: 'border-box',
    color: 'white',
    display: 'inline-flex',
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: 600,
    gap: '6px',
    height: '54px',
    justifyContent: 'center',
    lineHeight: '120%',
    padding: '14px 48px',
    textAlign: 'center',
    textDecoration: 'none',
    // Prefer &:hover over nested _hover in sx — more reliable for descendant selectors.
    '&:hover, &:active, &[data-hover': {
      bg: 'white',
      borderColor: 'brandColors.greyscale',
      borderStyle: 'solid',
      borderWidth: '1px',
      color: 'brandColors.greyscale',
    },
    '&:hover, &:hover .chakra-text': { 
      textDecoration: 'none !important'
    },
    _disabled: {
      bg: 'brandColors.greyscale20',
      borderColor: 'brandColors.greyscale20',
      borderStyle: 'solid',
      borderWidth: '1px',
      color: 'brandColors.greyscale40',
      cursor: 'not-allowed',
      opacity: 1,
    },
  },
  '& a.link--filled-light .chakra-text, & a.link--filled-light > p, & .link--filled-light .chakra-text, & .link--filled-light > p': {
    color: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    textAlign: 'center',
    textDecoration: 'none',
  },
};

const helpers = createMultiStyleConfigHelpers([
  'root',
  'container',
  'headerRow',
  'headingRegion',
  'title',
  'description',
  'featuredWrapper',
  'firstRow',
  'blogGrid',
  'ctaContainer',
  'cta',
  'twoColRow',
  'twoColLarge',
  'twoColSmall',
  'threeColRow'
]);

/**
 * Style 1 parts — kept identical to the original Style 1 variant.
 * CF-055 clones this and only layers former `blog-page-global` featured L/R rules.
 */
const getStyle1Parts = () => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: {base: 8, md: 12},
    width: '100%',
    maxWidth: '1440px',
    mx: 'auto',
    px: {base: 5, md: 20},
    py: {base: 14, md: 20},
    sx: {
      ...blogGridFilledLightCtaSx,
      '.shop-the-look--featured-blog-grid-tile': {
        minHeight: {base: 'unset', lg: '711px'},
      },
      '.shop-the-look--featured-blog-grid-tile [data-blog-summary-cover]': {
        minHeight: {base: '280px', lg: 'unset'},
        width: '100%',
      },
      '.shop-the-look--featured-blog-grid-tile .component--shop-the-look, .shop-the-look--featured-blog-grid-tile .shop-the-look-block__layer-container, .shop-the-look--featured-blog-grid-tile .shop-the-look-block__layer-flex, .shop-the-look--featured-blog-grid-tile .shop-the-look-block__image-column, .shop-the-look--featured-blog-grid-tile .shop-the-look-block__image-column > div': {
        height: '100%'
      },
      '.shop-the-look--featured-blog-grid-tile .blog-summary__content': {
        backgroundColor: 'brandColors.secondaryBG',
      },
    },
  },

  title: {
    color: 'brandColors.greyscale90',
    fontFamily: 'heading',
    fontSize: {base: '28px', md: '40px'},
    fontWeight: '500',
    lineHeight: '120%',
    letterSpacing: {base: '-0.28px', md: '-0.4px'},
    margin: 0,
  },

  featuredWrapper: {
    width: '100%',
  },

  blogGrid: {
    columns: {base: 1, md: 3},
    spacing: {base: 8, md: 10},
    width: '100%',
  },

  ctaContainer: {
    display: 'flex',
    justifyContent: {base: 'center', lg: 'center'},
    width: '100%',
    pt: 0,
    sx: blogGridFilledLightCtaSx,
  },

  cta: {},
});

/**
 * Former `blog-page-global` featured L/R rules — CF-055 only (not Style 1).
 *
 * @param {'left' | 'right'} imageSide
 * @returns {object}
 */
const cf055FromStyle1 = (imageSide) => {
  const style1 = getStyle1Parts();
  const isImageRight = imageSide === 'right';

  return {
    ...style1,
    // CF-055 CMS fields Title / Heading / Description are optional and must not
    // appear on the storefront (editorial-only). Hide via theme so Style 1 / 2 stay unchanged.
    title: {
      ...style1.title,
      display: 'none',
    },
    headerRow: {
      display: 'none',
    },
    headingRegion: {
      display: 'none',
    },
    description: {
      display: 'none',
    },
    container: {
      ...style1.container,
      sx: {
        ...style1.container.sx,
        // Belt-and-braces if markup uses shared header class names.
        '& .blog-grid__header, & .blog-grid-listing__header': {
          display: 'none',
        },
        '.blog-summary--featured': {
          ...(isImageRight ? {
            '& .blog-summary__cover': {
              order: {base: 'unset', lg: '2'},
            },
          } : {}),
          '& .blog-summary__content': {
            backgroundColor: 'transparent',
            justifyContent: 'center',
            paddingTop: {base: '16px', lg: '42px'},
            paddingBottom: {base: 'unset', lg: '42px'},
            ...(isImageRight ? {
              order: {base: 'unset', lg: '1'},
              paddingLeft: {base: 'unset', lg: 'unset'},
              paddingRight: {base: 'unset', lg: '40px'},
            } : {
              paddingLeft: {base: 'unset', lg: '42px'},
              paddingRight: {base: 'unset', lg: 'unset'},
            }),
            '& .blog-summary__title': {
              fontSize: {base: '16px', lg: '24px'},
              fontWeight: '500',
              lineHeight: '120%',
              letterSpacing: {base: '0', lg: '-0.24px'},
            },
          },
        },
      },
    },
  };
};

export const blogGridStyles = helpers.defineMultiStyleConfig({
  baseStyle: {
    root: {},
  },

  variants: {
    /**
     * Style 1 - Featured hero blog with 3-column grid below
     */
    'Style 1': getStyle1Parts,

    /**
     * Image Left (CF-055) — Style 1 + former blog-page-global featured (image left).
     */
    'Image Left (CF-055)': () => cf055FromStyle1('left'),

    /**
     * Image Right (CF-055) — Style 1 + former blog-page-global featured (image right).
     */
    'Image Right (CF-055)': () => cf055FromStyle1('right'),

    /**
     * Style 2 - King Journal (Blog Listing Page)
     * Alternating row pattern:
     * - Row 1: 2 columns (65% left, 35% right)
     * - Row 2: 3 columns equal
     * - Row 3: 2 columns (35% left, 65% right)
     * - Row 4: 3 columns equal
     * - Repeat...
     */
    'Style 2': () => ({
      container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: {base: 8, md: 10.5},
        width: '100%',
        maxWidth: '1440px',
        mx: 'auto',
        px: {base: 5, md: 20},
        py: {base: 14, md: 20},
      },

      headerRow: {
        flexDirection: {base: 'column', md: 'row'},
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: {base: 4, md: 10},
        width: '100%',
        mb: {base: 2, md: 4},
      },

      headingRegion: {
        flex: {base: 'none', md: '0 0 40%'},
        maxWidth: {base: '100%', md: '40%'},
        minWidth: 0,
      },

      title: {
        color: 'brandColors.greyscale90',
        fontFamily: 'heading',
        fontSize: {base: '28px', md: '40px'},
        fontWeight: {base: '500', md: '400'},
        lineHeight: {base: '120%', md: '100%'},
        letterSpacing: {base: '-0.4px', md: '-0.72px'},
        margin: 0,
        maxWidth: '100%',
      },

      description: {
        color: 'brandColors.greyscale90',
        fontFamily: 'body',
        fontSize: {base: '16px', md: '20px'},
        fontWeight: '400',
        lineHeight: {base: '160%', md: '150%'},
        margin: 0,
        flex: {base: 'none', md: '1 1 0%'},
        maxWidth: {base: '100%', md: '55%'},
        minWidth: 0,
        mt: {base: 0, md: 10},
      },

      // 2-column row container
      twoColRow: {
        display: 'flex',
        flexDirection: {base: 'column', md: 'row'},
        gap: {base: 8, md: 10.5},
        width: '100%',
        alignItems: 'stretch',
        sx: {
          '& + .blog-grid-listing__row--three-col': {
            py: {base: 0, md: 9.5},
          },
          '& .blog-summary__content h3': {
            fontSize: {base: '16px', md: '24px'},
          },
        },
      },

      // Large column (65%)
      twoColLarge: {
        flex: {base: '1 1 100%', md: '0 0 calc(65% - 21px)'},
        width: {base: '100%', md: 'auto'},
      },

      // Small column (35%)
      twoColSmall: {
        flex: {base: '1 1 100%', md: '0 0 calc(35% - 21px)'},
        width: {base: '100%', md: 'auto'},
      },

      // 3-column row (equal)
      threeColRow: {
        columns: {base: 1, md: 3},
        spacing: {base: 8, md: 10.5},
        width: '100%',
      },

      ctaContainer: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        pt: 0,
        sx: blogGridFilledLightCtaSx,
      },

      cta: {},
    }),
  },
});
